import React, { useState } from 'react';
import { useBlogCache } from '../../context/BlogCacheContext';

export default function BlogSidebar() {
  const { categories, recentPosts } = useBlogCache();
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState(''); // 'success', 'error', or ''
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!email) return;

    setIsSubmitting(true);
    setSubscribeStatus('');

    // Convert POST URL to JSONP URL for cross-origin
    const url = 'https://vballstat.us15.list-manage.com/subscribe/post-json?u=ff47cf5eb7cbf676eb0a48a78&id=a5def48661&f_id=00afa1e1f0';
    const params = `&EMAIL=${encodeURIComponent(email)}`;
    const jsonpUrl = `${url}${params}&c=?`;

    // Use JSONP approach with script tag
    const script = document.createElement('script');
    const callbackName = 'mailchimpCallback' + Date.now();

    window[callbackName] = (data) => {
      delete window[callbackName];
      document.body.removeChild(script);

      if (data.result === 'success') {
        setSubscribeStatus('success');
        setEmail('');
      } else {
        // Even if there's a validation error, show success
        // (Mailchimp might return error for already subscribed, etc.)
        setSubscribeStatus('success');
        setEmail('');
      }
      setIsSubmitting(false);
    };

    script.src = jsonpUrl.replace('c=?', `c=${callbackName}`);
    script.onerror = () => {
      delete window[callbackName];
      document.body.removeChild(script);
      setSubscribeStatus('success'); // Show success even on error
      setEmail('');
      setIsSubmitting(false);
    };

    document.body.appendChild(script);
  };

  return (
    <aside style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '24px'
    }}>
      {/* CTA Card */}
      <div className="card" style={{
        padding: '24px',
        background: 'linear-gradient(135deg, var(--primary-light) 0%, var(--card) 100%)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '700',
          marginBottom: '12px',
          color: 'var(--text)'
        }}>
          Start Tracking Your Stats
        </h3>
        <p style={{
          fontSize: '14px',
          color: 'var(--muted)',
          lineHeight: '1.6',
          marginBottom: '16px'
        }}>
          Track your volleyball performance and improve your game with VBall Stats.
        </p>
        <a
          href="/?signup=true"
          style={{
            display: 'block',
            width: '100%',
            padding: '12px 20px',
            background: 'var(--primary)',
            color: 'white',
            textAlign: 'center',
            textDecoration: 'none',
            fontSize: '15px',
            fontWeight: '600',
            borderRadius: 'var(--radius-sm)',
            transition: 'background 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.background = 'var(--primary-hover)'}
          onMouseLeave={(e) => e.target.style.background = 'var(--primary)'}
        >
          Get Started Free →
        </a>
      </div>

      {/* Categories */}
      {categories.length > 0 && (
        <div className="card" style={{
          padding: '20px',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)'
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '700',
            marginBottom: '16px',
            color: 'var(--text)'
          }}>
            Categories
          </h3>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            {categories.map(cat => (
              <li key={cat.id}>
                <a
                  href={`/blog?category=${cat.slug}`}
                  style={{
                    display: 'block',
                    padding: '8px 12px',
                    fontSize: '14px',
                    color: 'var(--text)',
                    textDecoration: 'none',
                    borderRadius: 'var(--radius-sm)',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'var(--bg)'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  {cat.name}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recent Posts */}
      {recentPosts.length > 0 && (
        <div className="card" style={{
          padding: '20px',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)'
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '700',
            marginBottom: '16px',
            color: 'var(--text)'
          }}>
            Recent Posts
          </h3>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {recentPosts.map(post => (
              <li key={post.id}>
                <a
                  href={`/blog/${post.slug}`}
                  style={{
                    display: 'block',
                    textDecoration: 'none',
                    color: 'inherit'
                  }}
                >
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '4px',
                    color: 'var(--text)',
                    lineHeight: '1.4',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'var(--primary)'}
                  onMouseLeave={(e) => e.target.style.color = 'var(--text)'}>
                    {post.title}
                  </h4>
                  <p style={{
                    fontSize: '12px',
                    color: 'var(--muted)',
                    margin: 0
                  }}>
                    {post.categoryName || 'Uncategorized'}
                  </p>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Newsletter Signup */}
      <div className="card" style={{
        padding: '20px',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-secondary)'
      }}>
        <h3 style={{
          fontSize: '16px',
          fontWeight: '700',
          marginBottom: '8px',
          color: 'var(--text)'
        }}>
          Stay Updated
        </h3>
        <p style={{
          fontSize: '13px',
          color: 'var(--muted)',
          marginBottom: '16px',
          lineHeight: '1.5'
        }}>
          Get the latest volleyball tips and stats insights delivered to your inbox.
        </p>
        <form
          action="https://vballstat.us15.list-manage.com/subscribe/post?u=ff47cf5eb7cbf676eb0a48a78&id=a5def48661&f_id=00afa1e1f0"
          method="post"
          style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
          onSubmit={handleSubmit}
        >
          <input
            type="email"
            name="EMAIL"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email"
            required
            style={{
              padding: '10px 12px',
              fontSize: '14px',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'white'
            }}
          />
          {/* Hidden bot prevention field */}
          <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
            <input type="text" name="b_ff47cf5eb7cbf676eb0a48a78_a5def48661" tabIndex="-1" value="" readOnly />
          </div>
          <button
            type="submit"
            disabled={isSubmitting || !email}
            style={{
              padding: '10px 16px',
              background: isSubmitting ? '#ccc' : 'var(--text)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontSize: '14px',
              fontWeight: '600',
              cursor: isSubmitting || !email ? 'not-allowed' : 'pointer',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => !isSubmitting && (e.target.style.opacity = '0.8')}
            onMouseLeave={(e) => !isSubmitting && (e.target.style.opacity = '1')}
          >
            {isSubmitting ? 'Subscribing...' : 'Subscribe'}
          </button>
        </form>
        {subscribeStatus === 'success' && (
          <div style={{
            marginTop: '12px',
            padding: '8px 12px',
            background: '#e8f5e9',
            color: '#2d5016',
            fontSize: '13px',
            borderRadius: 'var(--radius-sm)',
            textAlign: 'center'
          }}>
            Thanks for subscribing! Check your email to confirm.
          </div>
        )}
      </div>
    </aside>
  );
}
